import { useMemo, useState } from "react";
import { useFormik } from "formik";
import {
  Clock,
  Gauge,
  Languages,
  ListChecks,
  PenSquare,
  PlusCircle,
  RefreshCw,
  Sparkles,
  Trash2,
} from "lucide-react";
import {
  createQuestion,
  deleteQuestion,
  updateQuestion,
} from "@/lib/api";
import type { Question } from "@/types/api";
import { formatNumber } from "@/lib/utils";

type ManageQuestionsProps = {
  initialQuestions: Question[];
};

type QuestionDraft = {
  id?: string | null;
  text_en: string;
  text_hi?: string | null;
  text_ru?: string | null;
  cooldown: number;
  time: number;
  marks: number;
  image?: string | null;
  options: Array<{
    id?: string | null;
    text_en: string;
    text_hi?: string | null;
    text_ru?: string | null;
    is_correct: boolean;
  }>;
};

const emptyOption = (isCorrect = false) => ({
  id: undefined,
  text_en: "",
  text_hi: "",
  text_ru: "",
  is_correct: isCorrect,
});

const emptyDraft: QuestionDraft = {
  text_en: "",
  text_hi: "",
  text_ru: "",
  cooldown: 5,
  time: 30,
  marks: 10,
  image: "",
  options: [emptyOption(true), emptyOption()],
};

function mapQuestionToDraft(question: Question): QuestionDraft {
  return {
    id: question.id ?? undefined,
    text_en: question.text.en,
    text_hi: question.text.hi ?? "",
    text_ru: question.text.ru ?? "",
    cooldown: question.cooldown,
    time: question.time,
    marks: question.marks,
    image: question.image ?? "",
    options: question.options.map((option) => ({
      id: option.id ?? undefined,
      text_en: option.text.en,
      text_hi: option.text.hi ?? "",
      text_ru: option.text.ru ?? "",
      is_correct: Boolean(option.is_correct),
    })),
  };
}

export function ManageQuestions({ initialQuestions }: ManageQuestionsProps) {
  const [questions, setQuestions] = useState(initialQuestions);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [initialValues, setInitialValues] =
    useState<QuestionDraft>(emptyDraft);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<"success" | "error">("success");

  const selectedQuestion = useMemo(
    () => questions.find((item) => item.id === selectedId),
    [questions, selectedId],
  );

  const showMessage = (variant: "success" | "error", text: string) => {
    setTone(variant);
    setMessage(text);
    setTimeout(() => setMessage(null), 5000);
  };

  const formik = useFormik<QuestionDraft>({
    initialValues,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      const { setSubmitting, resetForm, setValues } = helpers;

      const primaryPrompt =
        values.text_en.trim() ||
        values.text_hi?.trim() ||
        values.text_ru?.trim();

      if (!primaryPrompt) {
        showMessage(
          "error",
          "Provide the prompt in at least one language (English, Hindi, or Russian).",
        );
        setSubmitting(false);
        return;
      }

      if (!values.options.some((option) => option.is_correct)) {
        showMessage("error", "Select one correct answer before saving.");
        setSubmitting(false);
        return;
      }

      let optionsPayload;
      try {
        optionsPayload = values.options.map((option, index) => {
          const primaryOptionText =
            option.text_en.trim() ||
            option.text_hi?.trim() ||
            option.text_ru?.trim();

          if (!primaryOptionText) {
            throw new Error(
              `Option ${index + 1} requires text in at least one language.`,
            );
          }

          return {
            id: option.id,
            text: {
              en: primaryOptionText,
              hi: option.text_hi?.trim() || undefined,
              ru: option.text_ru?.trim() || undefined,
            },
            is_correct: option.is_correct,
          };
        });
      } catch (error) {
        showMessage(
          "error",
          error instanceof Error
            ? error.message
            : "Each option needs text in at least one language.",
        );
        setSubmitting(false);
        return;
      }

      const payload = {
        id: values.id,
        text: {
          en: primaryPrompt,
          hi: values.text_hi?.trim() || undefined,
          ru: values.text_ru?.trim() || undefined,
        },
        cooldown: Number(values.cooldown),
        time: Number(values.time),
        marks: Number(values.marks),
        image: values.image?.trim() || undefined,
        options: optionsPayload,
        correct_option_id:
          values.options.find((option) => option.is_correct)?.id ?? null,
      };

      try {
        const result = selectedId
          ? await updateQuestion(selectedId, payload as Question)
          : await createQuestion(payload as Question);

        if (!result) {
          showMessage("error", "Unable to persist question.");
          setSubmitting(false);
          return;
        }

        if (selectedId) {
          setQuestions((prev) =>
            prev.map((question) =>
              question.id === selectedId ? result : question,
            ),
          );
          const updatedDraft = mapQuestionToDraft(result);
          setInitialValues(updatedDraft);
          setValues(updatedDraft);
          showMessage("success", "Question updated.");
        } else {
          setQuestions((prev) => [result, ...prev]);
          showMessage("success", "Question created.");
          const fresh = emptyDraft;
          setInitialValues(fresh);
          resetForm({ values: fresh });
          setSelectedId(null);
        }
      } catch (error) {
        showMessage(
          "error",
          error instanceof Error
            ? error.message
            : "Unable to persist question.",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const resetDraft = () => {
    const fresh = emptyDraft;
    setSelectedId(null);
    setInitialValues(fresh);
    formik.resetForm({ values: fresh });
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    try {
      await deleteQuestion(selectedId);
      setQuestions((prev) =>
        prev.filter((question) => question.id !== selectedId),
      );
      showMessage("success", "Question removed.");
      resetDraft();
    } catch (error) {
      showMessage(
        "error",
        error instanceof Error ? error.message : "Unable to delete question.",
      );
    }
  };

  const markCorrectOption = (optionIndex: number) => {
    const nextOptions = formik.values.options.map((option, index) => ({
      ...option,
      is_correct: index === optionIndex,
    }));
    formik.setFieldValue("options", nextOptions);
  };

  const updateOptionField = <K extends keyof QuestionDraft["options"][number]>(
    optionIndex: number,
    key: K,
    value: QuestionDraft["options"][number][K],
  ) => {
    const nextOptions = [...formik.values.options];
    nextOptions[optionIndex] = {
      ...nextOptions[optionIndex],
      [key]: value,
    };
    formik.setFieldValue("options", nextOptions);
  };

  const addOption = () => {
    const hasCorrect = formik.values.options.some(
      (option) => option.is_correct,
    );
    formik.setFieldValue("options", [
      ...formik.values.options,
      emptyOption(!hasCorrect),
    ]);
  };

  const removeOption = (optionIndex: number) => {
    const filtered = formik.values.options.filter(
      (_, index) => index !== optionIndex,
    );
    const nextOptions = filtered.length ? filtered : [emptyOption(true)];
    if (!nextOptions.some((option) => option.is_correct)) {
      nextOptions[0] = { ...nextOptions[0], is_correct: true };
    }
    formik.setFieldValue("options", nextOptions);
  };

  const totalQuestions = questions.length;
  const localizedCount = questions.filter(
    (question) => question.text.hi || question.text.ru,
  ).length;
  const localizedCoverage = totalQuestions
    ? Math.round((localizedCount / totalQuestions) * 100)
    : 0;
  const isEditing = Boolean(selectedQuestion);
  const activeTitle =
    formik.values.text_en ||
    formik.values.text_hi ||
    formik.values.text_ru ||
    "Untitled question";

  return (
    <section className="space-y-6">
      <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 p-6 text-white shadow-md">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 text-sm font-semibold uppercase tracking-wide text-neutral-300">
              <Sparkles className="h-5 w-5" aria-hidden />
              Question authoring workspace
            </div>
            <h3 className="mt-3 text-2xl font-semibold text-white">
              {isEditing ? `Editing: ${activeTitle}` : "Start a new question draft"}
            </h3>
            <p className="mt-2 text-sm text-neutral-300">
              Capture localized prompts, configure timing, and curate answer options. Use the chips below to jump back into any existing question.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={resetDraft}
              className="inline-flex items-center gap-2 rounded-md border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white transition hover:bg-white/20"
            >
              <RefreshCw className="h-4 w-4" aria-hidden />
              New draft
            </button>
            {selectedId ? (
              <button
                type="button"
                onClick={handleDelete}
                className="inline-flex items-center gap-2 rounded-md border border-white/30 bg-white/10 px-4 py-2 text-sm font-medium text-red-200 transition hover:bg-white/20 hover:text-white"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </button>
            ) : null}
          </div>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center gap-3">
              <PenSquare className="h-5 w-5 text-neutral-200" aria-hidden />
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-300">
                  Total questions
                </p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(totalQuestions)}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center gap-3">
              <Languages className="h-5 w-5 text-neutral-200" aria-hidden />
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-300">
                  Multilingual coverage
                </p>
                <p className="mt-1 text-xl font-semibold">
                  {localizedCoverage}%
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/5 p-4">
            <div className="flex items-center gap-3">
              <ListChecks className="h-5 w-5 text-neutral-200" aria-hidden />
              <div>
                <p className="text-xs uppercase tracking-wide text-neutral-300">
                  Options per question
                </p>
                <p className="mt-1 text-xl font-semibold">
                  {formatNumber(formik.values.options.length)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {questions.length ? (
        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white/60 p-4 shadow-sm backdrop-blur-sm">
          <p className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
            Existing questions
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {questions.map((question) => {
              const label =
                question.text.en ||
                question.text.hi ||
                question.text.ru ||
                "Untitled";
              const isActive = selectedId === question.id;
              return (
                <button
                  type="button"
                  key={question.id ?? label}
                  onClick={() => {
                    if (!question.id) return;
                    setSelectedId(question.id);
                    const mapped = mapQuestionToDraft(question);
                    setInitialValues(mapped);
                    formik.setValues(mapped);
                  }}
                  className={`group inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                    isActive
                      ? "border-neutral-900 bg-neutral-900 text-white shadow-sm"
                      : "border-neutral-200 bg-white text-neutral-600 hover:border-neutral-400 hover:text-neutral-900"
                  }`}
                >
                  <PenSquare className="h-3.5 w-3.5" aria-hidden />
                  <span className="max-w-[160px] truncate">{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 p-6 text-sm text-neutral-600">
          No saved questions yet. Start drafting your first localized prompt using the form below.
        </div>
      )}

      {message ? (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            tone === "success"
              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
              : "border-red-200 bg-red-50 text-red-600"
          }`}
        >
          {message}
        </div>
      ) : null}

      <div className="rounded-2xl border border-neutral-200 bg-white shadow-sm">
        <form onSubmit={formik.handleSubmit} className="space-y-8 p-6">
          <div className="space-y-4 rounded-xl border border-neutral-200 bg-neutral-50/70 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
              <Languages className="h-4 w-4 text-neutral-500" aria-hidden />
              Multilingual prompt
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                <span className="font-medium text-neutral-700">Prompt (English)</span>
                <textarea
                  name="text_en"
                  value={formik.values.text_en}
                  onChange={formik.handleChange}
                  className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                  rows={3}
                  placeholder="English prompt (optional if localized)"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-neutral-700">Prompt (Hindi)</span>
                <textarea
                  name="text_hi"
                  value={formik.values.text_hi ?? ""}
                  onChange={formik.handleChange}
                  className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                  rows={3}
                  placeholder="हिंदी अनुवाद"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-neutral-700">Prompt (Russian)</span>
                <textarea
                  name="text_ru"
                  value={formik.values.text_ru ?? ""}
                  onChange={formik.handleChange}
                  className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                  rows={3}
                  placeholder="Русский перевод"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm sm:col-span-2">
                <span className="font-medium text-neutral-700">Reference image URL</span>
                <input
                  type="url"
                  name="image"
                  value={formik.values.image ?? ""}
                  onChange={formik.handleChange}
                  placeholder="https://..."
                  className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                />
              </label>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <label className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm">
              <span className="inline-flex items-center gap-2 font-medium text-neutral-700">
                <Clock className="h-4 w-4 text-neutral-500" aria-hidden />
                Cooldown (seconds)
              </span>
              <input
                type="number"
                min={0}
                value={formik.values.cooldown}
                onChange={(event) =>
                  formik.setFieldValue("cooldown", Number(event.target.value))
                }
                className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm">
              <span className="inline-flex items-center gap-2 font-medium text-neutral-700">
                <Gauge className="h-4 w-4 text-neutral-500" aria-hidden />
                Time limit (seconds)
              </span>
              <input
                type="number"
                min={0}
                value={formik.values.time}
                onChange={(event) =>
                  formik.setFieldValue("time", Number(event.target.value))
                }
                className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-2 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-sm">
              <span className="inline-flex items-center gap-2 font-medium text-neutral-700">
                <ListChecks className="h-4 w-4 text-neutral-500" aria-hidden />
                Marks awarded
              </span>
              <input
                type="number"
                min={0}
                value={formik.values.marks}
                onChange={(event) =>
                  formik.setFieldValue("marks", Number(event.target.value))
                }
                className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
              />
            </label>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold text-neutral-800">
                Answer options
              </div>
              <button
                type="button"
                onClick={addOption}
                className="inline-flex items-center gap-2 rounded-md border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-100"
              >
                <PlusCircle className="h-4 w-4" aria-hidden />
                Add option
              </button>
            </div>
            <div className="space-y-3">
              {formik.values.options.map((option, index) => (
                <div
                  key={`option-${index}`}
                  className="rounded-xl border border-neutral-200 bg-neutral-50 p-4 shadow-sm"
                >
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <label className="flex flex-1 flex-col gap-1 text-sm">
                      <span className="font-medium text-neutral-700">
                        Option text (English)
                      </span>
                      <input
                        type="text"
                        value={option.text_en}
                        onChange={(event) =>
                          updateOptionField(index, "text_en", event.target.value)
                        }
                        className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                        placeholder="English option (optional if localized)"
                      />
                    </label>
                    <label className="inline-flex items-center gap-2 self-start rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700">
                      <input
                        type="radio"
                        name={`question-correct-${selectedId ?? "new"}`}
                        checked={option.is_correct}
                        onChange={() => markCorrectOption(index)}
                        className="h-4 w-4 border-neutral-300"
                      />
                      Correct answer
                    </label>
                  </div>
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium text-neutral-600">
                        Option (Hindi)
                      </span>
                      <input
                        type="text"
                        value={option.text_hi ?? ""}
                        onChange={(event) =>
                          updateOptionField(index, "text_hi", event.target.value)
                        }
                        className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                        placeholder="हिंदी अनुवाद"
                      />
                    </label>
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium text-neutral-600">
                        Option (Russian)
                      </span>
                      <input
                        type="text"
                        value={option.text_ru ?? ""}
                        onChange={(event) =>
                          updateOptionField(index, "text_ru", event.target.value)
                        }
                        className="rounded-md border border-neutral-200 px-3 py-2 text-sm focus:border-neutral-400 focus:outline-none"
                        placeholder="Русский перевод"
                      />
                    </label>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="inline-flex items-center gap-2 text-xs font-semibold text-neutral-500 hover:text-neutral-800"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                      Remove option
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={formik.isSubmitting}
              className="inline-flex items-center gap-2 rounded-md bg-neutral-900 px-4 py-2 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-70"
            >
              {formik.isSubmitting ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" aria-hidden />
                  {isEditing ? "Saving..." : "Creating..."}
                </>
              ) : isEditing ? (
                <>
                  <PenSquare className="h-4 w-4" aria-hidden />
                  Save changes
                </>
              ) : (
                <>
                  <PlusCircle className="h-4 w-4" aria-hidden />
                  Create question
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}


